import LayoutGrid from "@/components/layout-grid/layout-grid";
import { VRIntlProviderComponent } from "@/components/providers/intl-provider";
import Page, { PageLoader } from "@/components/ui/page";

import CockpitTimeline from "./components/cockpit-timeline";
import CognitiveLoad from "./components/cognitive-load/cognitive-load";
import CognitiveLoadBreakdown from "./components/cognitive-load-breakdown/cognitive-load-breakdown";
import ColorPhaseScore from "./components/color-phase-score/color-phase-score";
import ColumnEyeTracking from "./components/column-eye-tracking/column-eye-tracking";
import CrewCognitiveLoad from "./components/crew-cognitive-load/crew-cognitive-load";
import CycleContainer from "./components/cycle-container/cycle-container";
import AltitudeFlightPathXEyeTrackingCanvas from "./components/dual-components/altitude-flight-path-x-eye-tracking-canvas";
import EyeTrackingCanvasXCognitiveLoad from "./components/dual-components/eye-tracking-canvas-x-cognitive-load";
import FlightPathXCognitiveLoad from "./components/dual-components/flight-path-x-cognitive-load";
import FlightPathXEyeTrackingCanvas from "./components/dual-components/flight-path-x-eye-tracking-canvas";
import MultiFlightPathCanvasXAltitudeGrid from "./components/dual-components/multi-flight-path-canvas-x-altitude-grid";
import EyeTracking from "./components/eye-tracking/eye-tracking";
import FlightCircuit from "./components/flight-path/flight-circuit/flight-circuit";
import FlightPath from "./components/flight-path/flight-path";
import FlightPathAndEyeTracking from "./components/flight-path-and-eyetracking/flight-path-and-eyetracking";
import { LandingScore } from "./components/landing-score";
import MapDisplay from "./components/map-display/map-display";
import MapTimeline from "./components/map-timeline/map-timeline";
import OverallPhaseScore from "./components/overall-phase-score/overall-phase-score";
import { OverallScore } from "./components/overall-score";
import { PerformanceScore } from "./components/performance-score/performance-score";
import PhaseSummary from "./components/phase-summary/phase-summary";
import ProgressScore from "./components/progress-score/progress-score";
import ProgressScoreTable from "./components/progress-score-table/progress-score-table";
import ScoreRadarChart from "./components/radar-chart";
import RecognitionScore from "./components/recognition-score/recognition-score";
import ResponseTime from "./components/response-time/response-time";
import Results from "./components/results/results";
import StatsSummary from "./components/stats-summary/stats-summary";
import StatsSummaryWithDropdown from "./components/stats-summary-with-dropdown/stats-summary-with-dropdown";
import TimelineChart from "./components/timeline-chart/timeline-chart";

import { useSessionInfo } from "./hooks/useSessionInfo";

import { Main } from "./session-detail.styles";

const localeFn = (target: string) => import(`./locale/${target.toLowerCase()}.json`);

const SessionDetail = () => {
    const { getSessionSubtitleDetails, logoUrl, title, state } = useSessionInfo();

    const getComponentById = (component: string) => {
        switch (component) {
            case "LandingScore":
                return <LandingScore />;
            case "PerformanceScore":
                return <PerformanceScore />;
            case "MapTimeline":
                return <MapTimeline />;
            case "OverallScore":
                return <OverallScore />;
            case "CockpitTimeline":
                return <CockpitTimeline />;
            case "RadarChart":
                return <ScoreRadarChart />;

            //Heat Next imported components
            case "CognitiveLoad":
                return <CognitiveLoad />;
            case "ColumnEyeTracking":
                return <ColumnEyeTracking />;
            case "EyeTracking":
                return <EyeTracking />;
            case "EyeTrackingCanvasXCognitiveLoad": //Dual Component
                return <EyeTrackingCanvasXCognitiveLoad />;
            case "FlightPathXEyeTrackingCanvas": //Dual Component
                return <FlightPathXEyeTrackingCanvas />;
            case "FlightPathXCognitiveLoad": //Dual Component
                return <FlightPathXCognitiveLoad />;
            case "AltitudeFlightPathXEyeTrackingCanvas": //Dual Component
                return <AltitudeFlightPathXEyeTrackingCanvas />;
            case "MultiFlightPathCanvasXAltitudeGrid": //Dual Component
                return <MultiFlightPathCanvasXAltitudeGrid />;
            case "FlightPath": //FlightPath with playback using dual component
                return <FlightPath />;
            case "FlightCircuit":
                return <FlightCircuit />;
            case "FlightPathCanvas":
                return <FlightPathAndEyeTracking />;
            case "FlightPathAndEyeTracking":
                return <FlightPathAndEyeTracking />;
            case "MapDisplay": //previously MapTimeline
                return <MapDisplay />;
            case "Results": //previously QuestionResults
                return <Results />;
            case "RecognitionScore": //previously RecognitionScoring
                return <RecognitionScore />;
            case "TimelineChart":
                return <TimelineChart />;
            case "CrewCognitiveLoad":
                return <CrewCognitiveLoad />;
            case "CognitiveLoadBreakdown":
                return <CognitiveLoadBreakdown />;
            case "ResponseTime": //previously CommunicationTimeline
                return <ResponseTime />;
            case "ColorPhaseScore":
                return <ColorPhaseScore />;
            case "OverallPhaseScore":
                return <OverallPhaseScore />;
            case "StatsSummary":
                return <StatsSummary />;
            case "StatsSummaryWithDropdown":
                return <StatsSummaryWithDropdown />;
            case "ProgressScore":
                return <ProgressScore />;
            case "ProgressScoreTable":
                return <ProgressScoreTable />;
            case "CycleContainer":
                return <CycleContainer />;
            case "PhaseSummary":
                return <PhaseSummary />;
            default:
                return null;
        }
    };

    return (
        <Page
            pageTitle={false}
            title={title}
            showBackButton={true}
            showDetails={true}
            subtitleDetailText={getSessionSubtitleDetails}
            logoUrl={state?.logoUrl || logoUrl}
        >
            <Main>
                <LayoutGrid getComponentById={getComponentById} />
            </Main>
        </Page>
    );
};

const SessionDetailPage = () => (
    <VRIntlProviderComponent localeFn={localeFn} id="session-detail" fallback={<PageLoader />}>
        <SessionDetail />
    </VRIntlProviderComponent>
);

export default SessionDetailPage;
